import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Building2, Plus, Loader2, Edit3, Trash2, AlertTriangle, CheckCircle2,
  Clock, XCircle, Archive, Mail, FileText, ChevronLeft, History, Bell
} from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
const STATE_LABEL = {
  in_preparazione: _t("In preparazione"),
  attiva: _t("Attiva"),
  in_scadenza: _t("In scadenza"),
  scaduta: _t("Scaduta"),
  terminata: _t("Terminata"),
};

const STATE_PILL = {
  in_preparazione: "pill-bozza",
  attiva: "pill-aperta",
  in_scadenza: "pill-ripensamento",
  scaduta: "pill-disponibile",
  terminata: "pill-annullata",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(_locale(), { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / 86400000);
}

export default function AdminAgreements() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({ agreements: [], counters: {} });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list", agreement: null });
  const [typeFilter, setTypeFilter] = useState("bank"); // 'bank' | 'payment_provider'

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/agreements");
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  const filtered = {
    agreements: data.agreements.filter(a => (a.agreement_type || "bank") === typeFilter),
    counters: data.counters,
  };

  return (
    <AppLayout>
      <div className="mb-6 flex gap-2 border-b border-white/10" data-testid="admin-tabs">
        <a href="/admin/convenzioni" className="px-4 py-3 -mb-px text-sm border-b-2 border-[#FF5722] text-[#FF5722]">{_t("Convenzioni")}</a>
        <a href="/admin/consensi" className="px-4 py-3 -mb-px text-sm border-b-2 border-transparent text-white/60 hover:text-white">{_t("Consensi")}</a>
        <a href="/admin/pagamenti" className="px-4 py-3 -mb-px text-sm border-b-2 border-transparent text-white/60 hover:text-white">{_t("Pagamenti")}</a>
        <a href="/admin/utenti" className="px-4 py-3 -mb-px text-sm border-b-2 border-transparent text-white/60 hover:text-white">{_t("Utenti")}</a>
      </div>
      {view.mode === "list" && (
        <>
          <div className="mb-6 flex gap-2 border-b border-white/10">
            <button data-testid="tab-bank" onClick={() => setTypeFilter("bank")} className={`px-4 py-3 -mb-px text-sm border-b-2 ${typeFilter === "bank" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60"}`}>{_t("Convenzioni bancarie")}</button>
            <button data-testid="tab-provider" onClick={() => setTypeFilter("payment_provider")} className={`px-4 py-3 -mb-px text-sm border-b-2 ${typeFilter === "payment_provider" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60"}`}>{_t("Payment provider")}</button>
          </div>
          <ListView data={filtered} loading={loading} typeFilter={typeFilter} onCreate={() => setView({ mode: "create", initialType: typeFilter })} onView={(a) => setView({ mode: "detail", agreement: a })} />
        </>
      )}
      {view.mode === "create" && (
        <FormView initialType={view.initialType} onCancel={() => setView({ mode: "list" })} onSaved={() => { setView({ mode: "list" }); load(); }} />
      )}
      {view.mode === "edit" && (
        <FormView agreement={view.agreement} onCancel={() => setView({ mode: "detail", agreement: view.agreement })} onSaved={(a) => { setView({ mode: "detail", agreement: a }); load(); }} />
      )}
      {view.mode === "detail" && view.agreement && (
        <DetailView agreementId={view.agreement.id} onBack={() => { setView({ mode: "list" }); load(); }} onEdit={(a) => setView({ mode: "edit", agreement: a })} onChanged={() => load()} />
      )}
    </AppLayout>
  );
}

// ============ LIST ============
function ListView({ data, loading, typeFilter, onCreate, onView }) {
  const isProvider = typeFilter === "payment_provider";
  return (
    <>
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Admin · {0}", isProvider ? _t("Payment Provider") : _t("Convenzioni bancarie"))}</div>
          <h1 className="font-display text-5xl text-white leading-tight mb-2">{isProvider ? _t("Payment Provider") : _t("Convenzioni bancarie")}</h1>
          <p className="text-white/60 max-w-xl">{isProvider ? _t("Accordi con fornitori di servizi di pagamento (Stripe, Adyen, ecc.).") : _t("Accordi con istituti di credito depositari e storico stati.")}</p>
        </div>
        <button data-testid="new-agreement-btn" onClick={onCreate} className="btn-launch">
          <Plus className="w-4 h-4" /> {isProvider ? _t("Nuova convenzione provider") : _t("Nuova convenzione")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        {["attiva", "in_scadenza", "in_preparazione", "scaduta", "terminata"].map((s) => (
          <div key={s} className="tc-card p-4" data-testid={`counter-${s}`}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">{STATE_LABEL[s]}</div>
            <div className="font-display text-3xl text-white">{data.counters[s] || 0}</div>
          </div>
        ))}
      </div>

      {loading ? <Loader2 className="w-8 h-8 text-[#FF5722] animate-spin mx-auto my-16" /> : (
        data.agreements.length === 0 ? (
          <div className="tc-card p-16 text-center">
            <Building2 className="w-8 h-8 text-white/30 mx-auto mb-4" />
            <div className="font-display text-3xl text-white mb-2">{_t("Nessuna convenzione")}</div>
            <p className="text-white/50 mb-6">{_t("Crea la prima convenzione con un istituto di credito.")}</p>
            <button onClick={onCreate} className="btn-launch"><Plus className="w-4 h-4" />{" "}{_t("Nuova convenzione")}</button>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="agreements-list">
            {data.agreements.map((a) => (
              <li key={a.id} onClick={() => onView(a)} className="tc-card p-5 cursor-pointer" data-testid={`agreement-row-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Building2 className="w-4 h-4 text-[#FF5722] shrink-0" />
                      <div className="font-display text-xl text-white truncate">{a.institution_name}</div>
                      <span className={`pill ${STATE_PILL[a.state] || "pill-bozza"}`}>{STATE_LABEL[a.state]}</span>
                    </div>
                    <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1 ml-7">
                      <span>{_t("Referente:")}{" "}<span className="text-white/70">{a.contact_name}</span></span>
                      <span>{_t("Scadenza:")}{" "}<span className="text-white/70">{formatDate(a.end_date)}</span></span>
                      <span>{_t("Voucher:")}{" "}<span className="text-white/70">€{a.voucher_min_amount}–{a.voucher_max_amount}</span></span>
                      {a.state === "in_scadenza" && daysUntil(a.end_date) !== null && (
                        <span className="text-yellow-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {daysUntil(a.end_date)}{" "}{_t("giorni")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </>
  );
}

// ============ FORM ============
function FormView({ agreement, initialType, onCancel, onSaved }) {
  const isEdit = !!agreement;
  const today = new Date().toISOString().slice(0, 10);
  const oneYearLater = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState(agreement ? {
    ...agreement,
    supported_currencies_str: (agreement.supported_currencies || []).join(", "),
    start_date: agreement.start_date?.slice(0, 10),
    end_date: agreement.end_date?.slice(0, 10),
  } : {
    agreement_type: initialType || "bank",
    institution_name: "", institution_country: "IT", institution_authorization: "", institution_address: "",
    contact_name: "", contact_role: "", contact_email: "", contact_phone: "",
    fee_pct: 0.03, interest_rate_pct: 2.5, remuneration_model: "capitale_nominale_al_beneficiario",
    fund_guarantee_mode: "custody_segregata", redemption_mode: "bonifico_bancario",
    voucher_min_amount: 5, voucher_max_amount: 2000,
    start_date: today, end_date: oneYearLater,
    auto_renewal: false, renewal_notice_days: 60,
    obligations_platform: "", obligations_bank: "", confidentiality_clause: "", data_treatment_clause: "", notes: "",
    supported_currencies_str: _t("EUR, USD"),
    custody_mode: "", payout_mode: "bonifico_bancario", fund_guarantee: "", liability_clause: "",
  });
  const [saving, setSaving] = useState(false);
  const isProvider = form.agreement_type === "payment_provider";

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        supported_currencies: (form.supported_currencies_str || "").split(",").map(s => s.trim()).filter(Boolean),
        fee_pct: Number(form.fee_pct),
        interest_rate_pct: Number(form.interest_rate_pct),
        voucher_min_amount: Number(form.voucher_min_amount),
        voucher_max_amount: Number(form.voucher_max_amount),
        renewal_notice_days: Number(form.renewal_notice_days),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      };
      delete payload.supported_currencies_str;
      const res = isEdit
        ? await api.patch(`/admin/agreements/${agreement.id}`, payload)
        : await api.post("/admin/agreements", payload);
      toast.success(isEdit ? _t("Convenzione aggiornata") : _t("Convenzione creata"));
      onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={onCancel} className="btn-ghost mb-6"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{isEdit ? _t("Modifica") : _t("Nuova convenzione")}</div>
        <h1 className="font-display text-4xl text-white">{isEdit ? form.institution_name : _t("Aggiungi convenzione")}</h1>
      </div>

      <div className="space-y-8 max-w-4xl">
        <Section title={_t("Dati istituto")}>
          <Grid>
            <Field label={_t("Nome istituto *")}><input data-testid="ag-institution" value={form.institution_name} onChange={(e) => upd("institution_name", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Paese")}><input value={form.institution_country} onChange={(e) => upd("institution_country", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Autorizzazione (ABI, Bankit, ...)")}><input value={form.institution_authorization} onChange={(e) => upd("institution_authorization", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Indirizzo")}><input value={form.institution_address} onChange={(e) => upd("institution_address", e.target.value)} className="tc-input" /></Field>
          </Grid>
        </Section>

        <Section title={_t("Referente")}>
          <Grid>
            <Field label={_t("Nome *")}><input data-testid="ag-contact-name" value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Ruolo")}><input value={form.contact_role} onChange={(e) => upd("contact_role", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Email *")}><input data-testid="ag-contact-email" type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Telefono")}><input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className="tc-input" /></Field>
          </Grid>
        </Section>

        <Section title={_t("Condizioni economiche")}>
          <Grid>
            <Field label={_t("Commissione piattaforma")}><input type="number" step="0.001" value={form.fee_pct} onChange={(e) => upd("fee_pct", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Tasso stimato annuo (%)")}><input type="number" step="0.1" value={form.interest_rate_pct} onChange={(e) => upd("interest_rate_pct", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Modello remunerazione")}>
              <select value={form.remuneration_model} onChange={(e) => upd("remuneration_model", e.target.value)} className="tc-input">
                <option value="capitale_nominale_al_beneficiario">{_t("Capitale nominale al beneficiario")}</option>
                <option value="ripartizione_interessi">{_t("Ripartizione interessi")}</option>
                <option value="rendimento_minimo">{_t("Rendimento minimo garantito")}</option>
              </select>
            </Field>
            <Field label={_t("Modalità garanzia fondi")}>
              <select value={form.fund_guarantee_mode} onChange={(e) => upd("fund_guarantee_mode", e.target.value)} className="tc-input">
                <option value="custody_segregata">{_t("Custodia segregata")}</option>
                <option value="omnibus_contabilità_analitica">{_t("Omnibus con contabilità analitica")}</option>
              </select>
            </Field>
            <Field label={_t("Modalità riscatto")}>
              <select value={form.redemption_mode} onChange={(e) => upd("redemption_mode", e.target.value)} className="tc-input">
                <option value="bonifico_bancario">{_t("Bonifico bancario")}</option>
                <option value="altro">{_t("Altro")}</option>
              </select>
            </Field>
          </Grid>
        </Section>

        <Section title={_t("Limiti voucher")}>
          <Grid>
            <Field label={_t("Importo minimo (€)")}><input type="number" value={form.voucher_min_amount} onChange={(e) => upd("voucher_min_amount", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Importo massimo (€)")}><input type="number" value={form.voucher_max_amount} onChange={(e) => upd("voucher_max_amount", e.target.value)} className="tc-input" /></Field>
          </Grid>
        </Section>

        <Section title={_t("Durata e rinnovo")}>
          <Grid>
            <Field label={_t("Data inizio *")}><input data-testid="ag-start" type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Data fine *")}><input data-testid="ag-end" type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} className="tc-input" /></Field>
            <Field label={_t("Auto-rinnovo")}>
              <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={form.auto_renewal} onChange={(e) => upd("auto_renewal", e.target.checked)} className="accent-[#FF5722]" /> <span className="text-white/80">{_t("Sì, auto-rinnovo")}</span></label>
            </Field>
            <Field label={_t("Preavviso rinnovo (giorni)")}><input type="number" value={form.renewal_notice_days} onChange={(e) => upd("renewal_notice_days", e.target.value)} className="tc-input" /></Field>
          </Grid>
        </Section>

        <Section title={_t("Obblighi e clausole")}>
          <div className="space-y-4">
            <Field label={isProvider ? _t("Obblighi della piattaforma") : _t("Obblighi della piattaforma")}><textarea value={form.obligations_platform} onChange={(e) => upd("obligations_platform", e.target.value)} rows={3} className="tc-textarea !min-h-[80px]" /></Field>
            <Field label={isProvider ? _t("Obblighi del provider") : _t("Obblighi della banca")}><textarea value={form.obligations_bank} onChange={(e) => upd("obligations_bank", e.target.value)} rows={3} className="tc-textarea !min-h-[80px]" /></Field>
            <Field label={_t("Riservatezza")}><textarea value={form.confidentiality_clause} onChange={(e) => upd("confidentiality_clause", e.target.value)} rows={2} className="tc-textarea !min-h-[60px]" /></Field>
            <Field label={_t("Trattamento dati")}><textarea value={form.data_treatment_clause} onChange={(e) => upd("data_treatment_clause", e.target.value)} rows={2} className="tc-textarea !min-h-[60px]" /></Field>
            {isProvider && (
              <>
                <Field label={_t("Garanzia sul fondo (custodia del denaro bloccato)")}><textarea value={form.fund_guarantee} onChange={(e) => upd("fund_guarantee", e.target.value)} rows={2} className="tc-textarea !min-h-[60px]" placeholder={_t("Es. Fondi cliente segregati presso banca autorizzata (art. 23 PSD2).")} /></Field>
                <Field label={_t("Responsabilità")}><textarea value={form.liability_clause} onChange={(e) => upd("liability_clause", e.target.value)} rows={2} className="tc-textarea !min-h-[60px]" /></Field>
              </>
            )}
            <Field label={_t("Note")}><textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows={2} className="tc-textarea !min-h-[60px]" /></Field>
          </div>
        </Section>

        {isProvider && (
          <Section title={_t("Configurazione provider")}>
            <Grid>
              <Field label={_t("Valute supportate (separate da virgola)")}><input value={form.supported_currencies_str} onChange={(e) => upd("supported_currencies_str", e.target.value)} className="tc-input" placeholder={_t("EUR, USD, GBP")} /></Field>
              <Field label={_t("Modalità custodia denaro bloccato")}>
                <select value={form.custody_mode} onChange={(e) => upd("custody_mode", e.target.value)} className="tc-input">
                  <option value="">—</option>
                  <option value="escrow_terze_parti">{_t("Escrow terze parti")}</option>
                  <option value="istituto_moneta_elettronica">{_t("Istituto di moneta elettronica (PSD2)")}</option>
                  <option value="istituto_pagamento">{_t("Istituto di pagamento")}</option>
                  <option value="conto_segregato_banca">{_t("Conto segregato in banca")}</option>
                </select>
              </Field>
              <Field label={_t("Modalità erogazione al destinatario")}>
                <select value={form.payout_mode} onChange={(e) => upd("payout_mode", e.target.value)} className="tc-input">
                  <option value="bonifico_bancario">{_t("Bonifico bancario")}</option>
                  <option value="wallet_provider">{_t("Wallet del provider")}</option>
                  <option value="carta_prepagata">{_t("Carta prepagata")}</option>
                  <option value="altro">{_t("Altro")}</option>
                </select>
              </Field>
            </Grid>
          </Section>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <button onClick={onCancel} className="btn-ghost">{_t("Annulla")}</button>
          <button data-testid="save-agreement-btn" onClick={submit} disabled={saving} className="btn-launch">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Salvo...")}</> : (isEdit ? _t("Salva modifiche") : _t("Crea convenzione"))}
          </button>
        </div>
      </div>
    </>
  );
}

// ============ DETAIL ============
function DetailView({ agreementId, onBack, onEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/admin/agreements/${agreementId}`);
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agreementId]);

  const transition = async (new_state) => {
    const reason = prompt(_t("Motivo del passaggio a \"{0}\":", STATE_LABEL[new_state])) || "";
    setTransitioning(true);
    try {
      await api.post(`/admin/agreements/${agreementId}/state`, { new_state, reason });
      toast.success(_t("Stato aggiornato"));
      await load();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTransitioning(false); }
  };

  const doDelete = async () => {
    if (!confirm(_t("Eliminare definitivamente questa convenzione?"))) return;
    try {
      await api.delete(`/admin/agreements/${agreementId}`);
      toast.success(_t("Eliminata"));
      onBack();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <Loader2 className="w-8 h-8 animate-spin text-[#FF5722] mx-auto my-16" />;
  const a = data.agreement;
  const days = daysUntil(a.end_date);

  return (
    <>
      <button onClick={onBack} className="btn-ghost mb-6"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>

      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className={`pill ${STATE_PILL[a.state]}`}>{STATE_LABEL[a.state]}</span>
            {a.state === "in_scadenza" && <span className="text-yellow-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{" "}{_t("Scade tra")}{" "}{days}{" "}{_t("giorni")}</span>}
          </div>
          <h1 className="font-display text-4xl text-white">{a.institution_name}</h1>
          <p className="text-white/50 text-sm mt-1">{a.institution_authorization}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(a.state === "in_preparazione" || a.state === "attiva" || a.state === "in_scadenza") && (
            <button onClick={() => onEdit(a)} className="btn-ghost"><Edit3 className="w-4 h-4" />{" "}{_t("Modifica")}</button>
          )}
          {a.state === "in_preparazione" && (
            <button data-testid="activate-btn" onClick={() => transition("attiva")} disabled={transitioning} className="btn-launch"><CheckCircle2 className="w-4 h-4" />{" "}{_t("Attiva")}</button>
          )}
          {(a.state === "attiva" || a.state === "in_scadenza") && (
            <button data-testid="terminate-btn" onClick={() => transition("terminata")} disabled={transitioning} className="btn-danger"><XCircle className="w-4 h-4" />{" "}{_t("Termina")}</button>
          )}
          {a.state === "scaduta" && (
            <button onClick={() => transition("terminata")} disabled={transitioning} className="btn-ghost"><Archive className="w-4 h-4" />{" "}{_t("Archivia")}</button>
          )}
          {a.state === "in_preparazione" && (
            <button onClick={doDelete} className="btn-danger"><Trash2 className="w-4 h-4" />{" "}{_t("Elimina")}</button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <InfoBlock title={_t("Istituto")} icon={<Building2 className="w-4 h-4" />}>
          <Row k={_t("Nome")} v={a.institution_name} />
          <Row k={_t("Paese")} v={a.institution_country} />
          <Row k={_t("Autorizzazione")} v={a.institution_authorization} />
          <Row k={_t("Indirizzo")} v={a.institution_address} />
        </InfoBlock>

        <InfoBlock title={_t("Referente")} icon={<Mail className="w-4 h-4" />}>
          <Row k={_t("Nome")} v={a.contact_name} />
          <Row k={_t("Ruolo")} v={a.contact_role} />
          <Row k={_t("Email")} v={a.contact_email} />
          <Row k={_t("Telefono")} v={a.contact_phone} />
        </InfoBlock>

        <InfoBlock title={_t("Condizioni economiche")} icon={<FileText className="w-4 h-4" />}>
          <Row k={_t("Commissione")} v={`${((a.fee_pct||0)*100).toFixed(2)}%`} />
          <Row k={_t("Tasso annuo")} v={`${a.interest_rate_pct}%`} />
          <Row k={_t("Modello remunerazione")} v={a.remuneration_model} />
          <Row k={_t("Garanzia fondi")} v={a.fund_guarantee_mode} />
          <Row k={_t("Riscatto")} v={a.redemption_mode} />
        </InfoBlock>

        <InfoBlock title={_t("Voucher & Durata")} icon={<Clock className="w-4 h-4" />}>
          <Row k={_t("Importi voucher")} v={`€${a.voucher_min_amount} – €${a.voucher_max_amount}`} />
          <Row k={_t("Data inizio")} v={formatDate(a.start_date)} />
          <Row k={_t("Data fine")} v={formatDate(a.end_date)} />
          <Row k={_t("Auto-rinnovo")} v={a.auto_renewal ? _t("Sì") : _t("No")} />
          <Row k={_t("Preavviso rinnovo")} v={`${a.renewal_notice_days} giorni`} />
        </InfoBlock>
      </div>

      {(a.obligations_platform || a.obligations_bank || a.confidentiality_clause || a.data_treatment_clause) && (
        <div className="tc-card p-6 mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">{_t("Obblighi e clausole")}</div>
          {a.obligations_platform && <ClauseBlock label={_t("Obblighi piattaforma")} text={a.obligations_platform} />}
          {a.obligations_bank && <ClauseBlock label={_t("Obblighi banca")} text={a.obligations_bank} />}
          {a.confidentiality_clause && <ClauseBlock label={_t("Riservatezza")} text={a.confidentiality_clause} />}
          {a.data_treatment_clause && <ClauseBlock label={_t("Trattamento dati")} text={a.data_treatment_clause} />}
          {a.notes && <ClauseBlock label={_t("Note")} text={a.notes} />}
        </div>
      )}

      {/* State history */}
      <div className="tc-card p-6 mb-6" data-testid="state-history">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><History className="w-4 h-4" />{" "}{_t("Storico stati")}</div>
        <ol className="space-y-3">
          {a.state_history.map((h, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-[#FF5722] mt-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap"><span className={`pill ${STATE_PILL[h.state]}`}>{STATE_LABEL[h.state]}</span>
                  <span className="text-xs text-white/50">{formatDate(h.at)} · {new Date(h.at).toLocaleTimeString(_locale(), {hour: "2-digit", minute:"2-digit"})}</span></div>
                {h.reason && <div className="text-sm text-white/70 mt-1">{h.reason}</div>}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Communication log */}
      <div className="tc-card p-6" data-testid="communication-log">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><Bell className="w-4 h-4" />{" "}{_t("Log comunicazioni automatiche")}</div>
        {data.communication_log.length === 0 ? (
          <div className="text-white/50 text-sm py-3">{_t("Nessuna comunicazione registrata.")}</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {data.communication_log.map((l) => (
              <li key={l.id} className="py-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">{l.kind}</span>
                  <span className="text-[10px] text-white/40">· {l.channel} · {l.delivery_status}</span>
                  <span className="text-[10px] text-white/40 ml-auto">{formatDate(l.sent_at)}</span>
                </div>
                <div className="text-sm text-white">{l.subject}</div>
                <div className="text-xs text-white/50 mt-1">→ {l.recipient}</div>
                <div className="text-xs text-white/60 mt-1">{l.body}</div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 text-xs text-white/40">{_t("Le email sono simulate in modalità DEMO. Il log è tracciato per audit.")}</div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF5722] mb-4">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return <div><label className="tc-label">{label}</label>{children}</div>;
}
function InfoBlock({ title, icon, children }) {
  return (
    <div className="tc-card p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">{icon} {title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-white/50 text-xs">{k}</span>
      <span className="text-white text-right truncate">{v || "—"}</span>
    </div>
  );
}
function ClauseBlock({ label, text }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{label}</div>
      <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
}
